import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getOrderById, getDeliveryByOrderId } from "../services/order.service";

const OrderDetail = () => {
    const { id } = useParams();
    const [order, setOrder] = useState(null);
    const [delivery, setDelivery] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOrderData = async () => {
            try {
                const [orderData, deliveryData] = await Promise.all([
                    getOrderById(id),
                    getDeliveryByOrderId(id)
                ]);
                setOrder(orderData);
                setDelivery(deliveryData);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchOrderData();
    }, [id]);

    if (loading) return <div className="py-80 text-center">Loading order details...</div>;
    if (error) return <div className="py-80 text-center text-danger">Error: {error}</div>;
    if (!order) return <div className="py-80 text-center">Order not found.</div>;

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <section className='order-detail py-80 bg-gray-50'>
            <div className='container container-lg'>
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="d-flex align-items-center gap-12 mb-32">
                            <Link to="/orders" className="w-40 h-40 flex-center bg-white border border-gray-100 rounded-circle text-gray-900 hover-bg-main-600 hover-text-white transition-2">
                                <i className="ph ph-arrow-left text-xl" />
                            </Link>
                            <h4 className="mb-0 fw-bold">Order Details</h4>
                        </div>

                        <div className="row gy-4">
                            <div className="col-lg-8">
                                {/* Order Status & ID */}
                                <div className="bg-white rounded-16 p-32 border border-gray-100 shadow-sm mb-24">
                                    <div className="flex-between flex-wrap gap-16 mb-24">
                                        <div>
                                            <span className="text-gray-400 text-sm d-block mb-4">Order ID</span>
                                            <h5 className="mb-0 fw-bold">#{order.id.toUpperCase()}</h5>
                                        </div>
                                        <div className="text-sm-end">
                                            <span className="text-gray-400 text-sm d-block mb-4">Placed on</span>
                                            <span className="fw-medium">{formatDate(order.createdAt)}</span>
                                        </div>
                                    </div>
                                    <div className="p-20 rounded-12 d-flex align-items-center gap-16" style={{ background: 'rgba(250, 100, 0, 0.05)' }}>
                                        <div className="w-48 h-48 rounded-circle bg-main-600 text-white flex-center text-2xl">
                                            <i className="ph-fill ph-package" />
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-500 d-block mb-2">Current Status</span>
                                            <span className="badge bg-main-600 text-white px-16 py-6 rounded-pill fw-bold">{order.status}</span>
                                        </div>
                                        {order.paymentMethod && (
                                            <div className="ms-auto text-end">
                                                <span className="text-sm text-gray-500 d-block mb-2">Payment Method</span>
                                                <span className="fw-bold text-gray-900">{order.paymentMethod}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Order Items */}
                                <div className="bg-white rounded-16 p-32 border border-gray-100 shadow-sm">
                                    <h6 className="mb-24 fw-bold">Ordered Items</h6>
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr className="border-bottom border-gray-100">
                                                    <th className="ps-0 py-12 text-gray-400 fw-medium text-sm">Product</th>
                                                    <th className="py-12 text-gray-400 fw-medium text-sm text-center">Price</th>
                                                    <th className="py-12 text-gray-400 fw-medium text-sm text-center">Qty</th>
                                                    <th className="pe-0 py-12 text-gray-400 fw-medium text-sm text-end">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {order.items?.map((item, idx) => (
                                                    <tr key={idx} className="border-bottom border-gray-50">
                                                        <td className="ps-0 py-20">
                                                            <div className="d-flex align-items-center gap-16">
                                                                <img 
                                                                    src={item.image || 'assets/images/thumbs/product-img1.png'} 
                                                                    alt="" 
                                                                    className="w-64 h-64 rounded-8 object-fit-cover border border-gray-100" 
                                                                />
                                                                <div>
                                                                    <Link to={`/product-details?id=${item.id}`} className="text-gray-900 fw-bold hover-text-main-600 d-block mb-4">
                                                                        {item.name}
                                                                    </Link>
                                                                    {item.variantName && (
                                                                        <span className="text-xs text-gray-500 bg-gray-50 px-8 py-2 rounded">
                                                                            Variant: {item.variantName}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-20 text-center fw-medium text-gray-900">
                                                            Rs.{(item.price || item.unitPrice)?.toFixed(0)}
                                                        </td>
                                                        <td className="py-20 text-center fw-medium text-gray-900">
                                                            {item.quantity}
                                                        </td>
                                                        <td className="pe-0 py-20 text-end fw-bold text-main-600">
                                                            Rs.{((item.price || item.unitPrice) * item.quantity).toFixed(0)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                {/* Shipping Address */}
                                <div className="bg-white rounded-16 p-24 border border-gray-100 shadow-sm mb-24">
                                    <h6 className="mb-16 fw-bold flex-align gap-8">
                                        <i className="ph ph-map-pin text-main-600" />
                                        Shipping Address
                                    </h6>
                                    {order.customerInfo ? (
                                        <div className="text-sm text-gray-600 leading-relaxed">
                                            <p className="fw-bold text-gray-900 mb-4">{order.customerInfo.name}</p>
                                            <p className="mb-2">{order.customerInfo.address}</p>
                                            <p className="mb-2">{order.customerInfo.city}, {order.customerInfo.state} - {order.customerInfo.postCode}</p>
                                            <p className="mb-0"><i className="ph ph-phone me-4" /> {order.customerInfo.phone}</p>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400 mb-0">Address information not available.</p>
                                    )}
                                </div>

                                {delivery && (
                                    <div className="bg-white rounded-16 p-24 border border-gray-100 shadow-sm mb-24">
                                        <h6 className="mb-16 fw-bold flex-align gap-8">
                                            <i className="ph ph-truck text-main-600" />
                                            Logistics & Tracking
                                        </h6>
                                        <div className="space-y-12">
                                            <div className="flex-between">
                                                <span className="text-gray-500 text-sm">Status</span>
                                                <span className="badge bg-main-100 text-main-600 px-12 py-4 rounded-pill fw-bold text-xs">
                                                    {delivery.status.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                            <div className="flex-between">
                                                <span className="text-gray-500 text-sm">Carrier</span>
                                                <span className="text-gray-900 fw-bold text-sm">{delivery.carrier || 'Processing'}</span>
                                            </div>
                                            {delivery.trackingId && (
                                                <div className="flex-between">
                                                    <span className="text-gray-500 text-sm">Tracking ID</span>
                                                    <span className="text-gray-900 fw-bold text-sm font-mono">{delivery.trackingId}</span>
                                                </div>
                                            )}
                                            <div className="flex-between pt-12 border-top border-gray-50">
                                                <span className="text-gray-500 text-sm">Est. Delivery</span>
                                                <span className="text-gray-900 fw-bold text-sm">
                                                    {delivery.estimatedDate ? new Date(delivery.estimatedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : 'Pending'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Order Summary */}
                                <div className="bg-white rounded-16 p-24 border border-gray-100 shadow-sm">
                                    <h6 className="mb-24 fw-bold">Order Summary</h6>
                                    <div className="space-y-16">
                                        <div className="flex-between gap-8">
                                            <span className="text-gray-600 text-sm">Subtotal</span>
                                            <span className="text-gray-900 fw-bold text-sm">Rs.{order.subtotal?.toFixed(0) || order.total?.toFixed(0)}</span>
                                        </div>
                                        <div className="flex-between gap-8">
                                            <span className="text-gray-600 text-sm">Tax</span>
                                            <span className="text-gray-900 fw-bold text-sm">Rs.{order.tax?.toFixed(0) || 0}</span>
                                        </div>
                                        <div className="flex-between gap-8">
                                            <span className="text-gray-600 text-sm">Shipping</span>
                                            <span className="text-gray-900 fw-bold text-sm">Rs.{order.shipping?.toFixed(0) || 0}</span>
                                        </div>
                                        <div className="pt-16 border-top border-gray-50 flex-between gap-8">
                                            <span className="text-gray-900 fw-bold">Total Amount</span>
                                            <span className="text-main-600 text-xl fw-bold">Rs.{order.total?.toFixed(0)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Need Help */}
                                <div className="mt-24 p-24 rounded-16 border border-main-100 bg-main-50">
                                    <h6 className="text-md fw-bold mb-8">Need Help?</h6>
                                    <p className="text-sm text-gray-600 mb-16">If you have any questions regarding your order, feel free to contact us.</p>
                                    <Link to="/contact" className="text-main-600 fw-bold text-sm hover-text-main-700">Contact Support →</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OrderDetail;
